/**
 * BulkActionsBar — the command surface for a selection.
 *
 * Rises from the bottom edge once two or more rows are selected and stays
 * there, out of the toolbar's way, until the selection is cleared. Dark and
 * raised in both palettes (the --command-* tokens in src/index.css). Each
 * button names one act; the act's dialog shows what is about to happen and
 * reports what saved. The two runs that have no dialog — asking an agent about
 * the selection, and Mark reviewed — keep their progress readout here.
 *
 * "Ask an agent" (O2, the bulk half): the only choice is the agent. Each entry
 * reads "Ask [agent] for these N" with the line under it saying who will decide
 * what the agent proposes. An agent that cannot run is not listed; when none
 * can, the menu is not drawn and one line names the fix instead — for a missing
 * model, the one shared line (AskAiNeedsKey).
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  Download,
  Archive,
  Loader2,
  Sparkles,
  UserCheck,
  Tag,
  Inbox,
  Flag,
  PlayCircle,
  SlidersHorizontal,
  CheckSquare,
} from 'lucide-react';
import { MARK_REVIEWED_LABEL, MARKING_REVIEWED_LABEL } from '../../../../lib/items/reviewVocabulary';
import { AskAiNeedsKey } from '../../../shared/AskAiNeedsKey';

export type BulkAct = 'owner' | 'tags' | 'queue' | 'status' | 'session' | 'field' | 'export' | 'retire';

/** The bar appears for a group, not for one row — a single row has its own menu. */
export const BULK_BAR_MIN_SELECTED = 2;

/** One agent the selection can be given to. */
export interface BulkAgentAskEntry {
  agentType: string;
  /** "Ask Value Estimator for these 14". */
  label: string;
  /** Who decides what it proposes: "Effort: AI recommends; you decide". */
  decides: string[];
  /** Set when more rows are chosen than one run reads: which it reads. */
  note?: string | null;
}

export interface BulkAgentAsk {
  entries: BulkAgentAskEntry[];
  /** Set when no agent can be asked: the one fix, and where it is made. */
  fix: { text: string; link?: { label: string; href: string } } | null;
  /** No agent can be asked because no AI model answers: the shared no-key line is drawn. */
  needsKey?: boolean;
  /** The run in progress, if one was started here. */
  running: { name: string; done: number; total: number; unit?: 'items' | 'estimates' } | null;
  onAsk: (agentType: string) => void;
}

export interface BulkActionsBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onClear: () => void;
  /** The bar names the act; the page opens its dialog. */
  onAct: (act: BulkAct) => void;
  /** Acts this person may take. An act that is not allowed is not shown. */
  allowed: Partial<Record<BulkAct, boolean>>;
  /** "Ask [agent] for these N" — the agents that can be asked, or the one fix when none can. */
  agentAsk?: BulkAgentAsk;
  /** Called when the user triggers a bulk "Mark as reviewed" run on selected items */
  onApproveAiItems?: () => void;
  /** Whether the button should be shown — true when the caller has the
   * items.approve_ai_provenance permission AND at least one selected item
   * has creationProvenance === 'ai_draft'. */
  approveAiAvailable?: boolean;
  /** Whether the bulk approve run is currently in progress — shows progress indicator */
  approveAiRunning?: boolean;
  /** Progress while running: done / total */
  approveAiProgress?: { done: number; total: number };
}

const ACTS: Array<{ act: BulkAct; label: string; icon: typeof Tag; title: (n: number) => string }> = [
  { act: 'owner', label: 'Owner', icon: UserCheck, title: (n) => `Give the ${n} selected one owner` },
  { act: 'tags', label: 'Tags', icon: Tag, title: (n) => `Add or remove tags on the ${n} selected` },
  { act: 'queue', label: 'Queue', icon: Inbox, title: (n) => `Move the ${n} selected to a queue` },
  { act: 'status', label: 'Status', icon: Flag, title: (n) => `Set the ${n} selected to Active, Deferred or Archived` },
  { act: 'session', label: 'Add to session', icon: PlayCircle, title: (n) => `Add the ${n} selected to a session's pool` },
  { act: 'field', label: 'Set field', icon: SlidersHorizontal, title: (n) => `Set category, altitude, source, bucket or dedupe on the ${n} selected` },
];

export function BulkActionsBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onClear,
  onAct,
  allowed,
  agentAsk,
  onApproveAiItems,
  approveAiAvailable = false,
  approveAiRunning = false,
  approveAiProgress,
}: BulkActionsBarProps) {
  const visible = selectedCount >= BULK_BAR_MIN_SELECTED;
  const anchorRef = useRef<HTMLSpanElement>(null);
  const inset = useHostInset(anchorRef, visible);
  if (!visible) return null;

  const acts = ACTS.filter((a) => allowed[a.act]);
  const busy = !!agentAsk?.running || approveAiRunning;

  return (
    <>
    {/* Marks where the bar lives in the page, so it can measure the list it acts on. */}
    <span ref={anchorRef} aria-hidden className="hidden" />
    <div
      role="toolbar"
      aria-label={`Actions on the ${selectedCount} selected items`}
      data-testid="bulk-actions-bar"
      // Fixed to the bottom of the window but laid out over the list it acts
      // on: its left and right edges are the list column's, so the app's fixed
      // side navigation (drawn above it) never covers its left end. Centred
      // there with auto margins.
      className="fixed bottom-6 z-40 mx-auto flex w-max flex-wrap items-center justify-center gap-0.5 border px-2 py-1.5"
      style={{
        left: inset.left,
        right: inset.right,
        maxWidth: `calc(100vw - ${inset.left + inset.right}px - 2rem)`,
        background: 'var(--command-bg)',
        color: 'var(--command-text)',
        borderColor: 'var(--command-rule)',
        boxShadow: 'var(--command-shadow)',
        animation: 'command-rise 180ms ease-out',
      }}
    >
      <span className="px-2 font-mono text-[0.75rem] uppercase tracking-[0.08em] tabular-nums">
        {selectedCount} selected
      </span>
      {selectedCount < totalCount && (
        <BarButton onClick={onSelectAll} title={`Select every item in this view (${totalCount})`} dim>
          <CheckSquare className="h-3.5 w-3.5" />
          All {totalCount}
        </BarButton>
      )}

      {acts.length > 0 && <Rule />}
      {acts.map(({ act, label, icon: Icon, title }) => (
        <BarButton key={act} onClick={() => onAct(act)} title={title(selectedCount)} disabled={busy}>
          <Icon className="h-3.5 w-3.5" />
          {label}
        </BarButton>
      ))}

      <Rule />
      {/* While "Mark reviewed" runs the agent menu is not drawn, rather than drawn disabled. */}
      {agentAsk && !approveAiRunning && <AgentAskControl ask={agentAsk} />}
      {onApproveAiItems && approveAiAvailable && (
        <BarButton
          onClick={onApproveAiItems}
          disabled={busy}
          title={
            approveAiRunning
              ? `${MARKING_REVIEWED_LABEL} ${approveAiProgress ? `${approveAiProgress.done} / ${approveAiProgress.total}` : ''}`
              : `Mark the AI-drafted items among the ${selectedCount} selected as reviewed`
          }
        >
          {approveAiRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {approveAiRunning && approveAiProgress ? `${approveAiProgress.done} / ${approveAiProgress.total}` : MARK_REVIEWED_LABEL}
        </BarButton>
      )}
      {allowed.export && (
        <BarButton onClick={() => onAct('export')} title="Download the selected items as a CSV file" disabled={busy}>
          <Download className="h-3.5 w-3.5" />
          Export
        </BarButton>
      )}
      {allowed.retire && (
        <BarButton
          onClick={() => onAct('retire')}
          title={`Retire the ${selectedCount} selected — they leave every list; records and history stay`}
          disabled={busy}
          danger
        >
          <Archive className="h-3.5 w-3.5" />
          Retire
        </BarButton>
      )}

      <Rule />
      <BarButton onClick={onClear} title="Clear the selection" aria-label="Clear selection" dim>
        <X className="h-4 w-4" />
      </BarButton>
    </div>
    </>
  );
}

/**
 * The agent menu, its progress while a run goes, or — when no agent can be
 * asked — the one line naming the fix. Never a disabled button.
 */
function AgentAskControl({ ask }: { ask: BulkAgentAsk }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  if (ask.running) {
    const { name, done, total, unit = 'items' } = ask.running;
    return (
      <span
        role="status"
        data-testid="bulk-agent-running"
        className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap px-2.5 font-mono text-[0.75rem] uppercase tracking-[0.08em]"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {name} {total > 0 ? `${done} of ${total} ${unit}` : '…'}
      </span>
    );
  }

  if (ask.entries.length === 0) {
    if (ask.needsKey) {
      // The shared line, in the bar's own dark colors (it reads the page's text tokens).
      return (
        <span
          className="max-w-[28rem] px-2.5"
          style={{ '--text-ter': 'var(--command-text-dim)', '--text-sec': 'var(--command-text)' } as CSSProperties}
        >
          <AskAiNeedsKey />
        </span>
      );
    }
    if (!ask.fix) return null;
    const { text, link } = ask.fix;
    // The link's words end the line ("… Add yours in Settings → My Connectors"); they become the link.
    const lead = link && text.endsWith(link.label) ? text.slice(0, -link.label.length) : link ? `${text} ` : text;
    return (
      <span data-testid="bulk-agent-fix" className="max-w-[28rem] px-2.5 text-[0.75rem] leading-snug" style={{ color: 'var(--command-text-dim)' }}>
        {lead}
        {link && (
          <Link to={link.href} className="underline" style={{ color: 'var(--command-text)' }}>
            {link.label}
          </Link>
        )}
      </span>
    );
  }

  return (
    <span ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Give the selected items to an agent"
        className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap px-2.5 font-mono text-[0.75rem] uppercase tracking-[0.08em] transition-colors hover:bg-[var(--command-hover)] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--mustard)]"
        style={{ color: 'var(--command-text)' }}
      >
        <Sparkles className="h-3.5 w-3.5" />
        Ask an agent
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Ask an agent"
          data-testid="bulk-agent-menu"
          className="absolute bottom-full left-0 mb-2 w-[22rem] max-w-[calc(100vw-2rem)] border py-1"
          style={{ background: 'var(--command-bg)', borderColor: 'var(--command-rule)', boxShadow: 'var(--command-shadow)' }}
        >
          {ask.entries.map((entry) => (
            <button
              key={entry.agentType}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                ask.onAsk(entry.agentType);
              }}
              className="block w-full px-3 py-2 text-left transition-colors hover:bg-[var(--command-hover)] focus:outline-none focus-visible:bg-[var(--command-hover)]"
            >
              <span className="block text-[0.8125rem] font-medium" style={{ color: 'var(--command-text)' }}>
                {entry.label}
              </span>
              {entry.decides.map((line) => (
                <span key={line} className="mt-0.5 block text-[0.75rem] leading-snug" style={{ color: 'var(--command-text-dim)' }}>
                  {line}
                </span>
              ))}
              {entry.note && (
                <span className="mt-0.5 block text-[0.75rem] leading-snug" style={{ color: 'var(--command-text-dim)' }}>
                  {entry.note}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function Rule() {
  return <span aria-hidden className="mx-1 h-5 w-px flex-shrink-0" style={{ background: 'var(--command-rule)' }} />;
}

function BarButton({
  children,
  onClick,
  title,
  disabled,
  danger,
  dim,
  'aria-label': ariaLabel,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  danger?: boolean;
  dim?: boolean;
  'aria-label'?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      disabled={disabled}
      className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap px-2.5 font-mono text-[0.75rem] uppercase tracking-[0.08em] transition-colors hover:bg-[var(--command-hover)] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--mustard)] disabled:cursor-not-allowed disabled:opacity-40"
      style={{ color: danger ? 'var(--command-danger)' : dim ? 'var(--command-text-dim)' : 'var(--command-text)' }}
    >
      {children}
    </button>
  );
}

/**
 * The left and right edges of the element the bar is rendered in, as distances
 * from the window's edges. Re-measured when that element changes size (the
 * side navigation collapsing, a panel opening) and when the window resizes.
 */
function useHostInset(anchor: RefObject<HTMLElement | null>, active: boolean): { left: number; right: number } {
  const [inset, setInset] = useState({ left: 0, right: 0 });
  useLayoutEffect(() => {
    if (!active) return;
    const host = anchor.current?.parentElement;
    if (!host) return;
    const measure = () => {
      const r = host.getBoundingClientRect();
      setInset({
        left: Math.max(0, Math.round(r.left)),
        right: Math.max(0, Math.round(window.innerWidth - r.right)),
      });
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(host);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [anchor, active]);
  return inset;
}
